import { Navigate, useLocation } from 'react-router-dom'
import type { ReactNode } from 'react'
import { useAuth } from './authContext'
import { paths } from './routes'

interface LocationState {
  from?: string
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth()
  const location = useLocation()

  if (!isAuthenticated) {
    // `replace` so the back glyph never walks the operator into a screen that cannot
    // load, and state carries where they were headed so re-login can return them.
    return <Navigate to={paths.login} replace state={{ from: location.pathname }} />
  }

  return children
}

export function RedirectIfSignedIn({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth()
  const { state } = useLocation()

  if (!isAuthenticated) {
    return children
  }

  // A session that died mid-aisle sends the operator here from the pallet they were
  // filling. Re-login returns them to that pallet rather than to the home screen, so the
  // interruption costs one login instead of a login plus finding the pallet again.
  return <Navigate to={resolveReturnTo(state)} replace />
}

function resolveReturnTo(state: unknown): string {
  const from = (state as LocationState | null)?.from

  // Only an in-app path — `//host` is protocol-relative, not local — and never back to
  // login, which would loop.
  if (
    typeof from === 'string' &&
    from.startsWith('/') &&
    !from.startsWith('//') &&
    from !== paths.login
  ) {
    return from
  }

  return paths.home
}
